import React, { memo } from 'react';
import {
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import Link from 'next/link';
import { PATH_DEMO, PATH_PRICING, PRODUCT_DEMO } from '@/constants/spa-routes';
import style from './Header.module.css';
import { Typography } from '@/components/Typography/Typography';
import Image from 'next/image';
import { clsx } from 'clsx';
import { NavigationItemsType } from '@/constants/navigation';

interface DesktopMenuProps {
  pathname: string;
  isIncludesRoutes: boolean;
  navigationItems: NavigationItemsType;
}
export const DesktopMenu = memo(
  ({ pathname, isIncludesRoutes, navigationItems }: DesktopMenuProps) => {
    return (
      <NavigationMenuList className="links lg:gap-[30px] gap-5">
        {navigationItems?.map((item) => {
          const dropdownLinksActive = item.dropdownItems
            ?.map((el) => el.items)
            .flat()
            ?.some((el) =>
              isIncludesRoutes ? pathname.includes(el?.href || '') : pathname === el?.href
            );
          return item.dropdownItems ? (
            <NavigationMenuItem
              key={`desktop-menu-dropdown-${item.id}-${item.title}`}
              className={clsx(style.desktopViewLi, 'relative')}
            >
              <div
                className={clsx(
                  dropdownLinksActive && 'h-[3px] w-full bg-ebony absolute -bottom-4'
                )}
              />
              <NavigationMenuTrigger className={clsx('p-0  text-ebony [&>svg>path]:stroke-ebony')}>
                <div className="flex items-end relative">
                  {item?.isNew && (
                    <Typography
                      as={'span'}
                      $type="PSmall"
                      $fontSizeSm="12px"
                      $fontSizeXs="12px"
                      $weight="500"
                      className="font-jamjuree pr-1"
                    >
                      new
                    </Typography>
                  )}

                  <Typography
                    $type="PSmall"
                    $fontSizeSm="14px"
                    $fontSizeXs="14px"
                    $weight={'600'}
                    className={clsx('font-jamjuree opacity-0')}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    $type="PSmall"
                    $fontSizeSm="14px"
                    $fontSizeXs="14px"
                    $weight={dropdownLinksActive ? '600' : '500'}
                    className={clsx(
                      'font-jamjuree absolute left-0',
                      !dropdownLinksActive &&
                        'opacity-80 hover:opacity-100 hover:!font-semibold transition-all'
                    )}
                  >
                    {item.title}
                  </Typography>
                </div>
              </NavigationMenuTrigger>
              <NavigationMenuContent className="left-1/2 transform -translate-x-1/2 pt-5 pb-3.5">
                {item.dropdownItems.map((dropdownItem, index) => {
                  return (
                    <div
                      key={dropdownItem.id}
                      className={clsx(
                        item?.dropdownItems?.length !== index + 1 && 'border-b border-[#05050D0D]',
                        'flex flex-col z-50 pt-5'
                      )}
                    >
                      <div className="flex items-center gap-2.5">
                        <Typography
                          $weight="600"
                          $fontSizeSm="13px"
                          $fontSizeXs="13px"
                          className="font-jamjuree opacity-30 uppercase"
                        >
                          {dropdownItem.title}
                        </Typography>
                        {dropdownItem?.isCategorySoon && (
                          <Typography
                            $type="PMini"
                            $fontSizeSm="10px"
                            $fontSizeXs="10px"
                            $weight="600"
                            className="uppercase bg-[#C4D1CE] rounded-full px-2 py-1 inline opacity-50"
                          >
                            soon
                          </Typography>
                        )}
                      </div>

                      <ul className="flex gap-[clamp(20px,70px,70px)]">
                        {dropdownItem?.items?.map((elements) => {
                          return (
                            <li
                              key={`${item.id}-${elements.title}`}
                              className={clsx(
                                'flex relative h-fit max-w-[200px] transition-all',
                                elements.comingSoon && 'pointer-events-none opacity-60'
                              )}
                            >
                              <div className="flex flex-col gap-2.5 py-5">
                                <NavigationMenuLink
                                  className="h-full [&_#menu-title]:hover:underline underline-offset-2"
                                  asChild
                                  aria-disabled={elements.comingSoon}
                                >
                                  <Link
                                    prefetch={false}
                                    className="flex flex-col justify-between !no-underline"
                                    href={elements.href}
                                  >
                                    <div className="flex gap-3">
                                      {elements.icon && (
                                        <div className="h-[35px] w-[35px] pointer-events-none">
                                          {typeof elements.icon === 'string' ? (
                                            <Image
                                              width="0"
                                              height="0"
                                              sizes="100vw"
                                              className={'w-full h-full object-contain'}
                                              alt={`${elements.title} image`}
                                              src={elements.icon}
                                            />
                                          ) : (
                                            elements.icon
                                          )}
                                        </div>
                                      )}
                                      <div className="flex flex-col gap-2">
                                        <div className="flex flex-col gap-0.5">
                                          <Typography
                                            id="menu-title"
                                            as={'span'}
                                            $type="PSmall"
                                            $fontSizeSm="13px"
                                            $fontSizeXs="13px"
                                            $weight="600"
                                            className={clsx('text-nowrap font-jamjuree')}
                                          >
                                            {elements.title}
                                          </Typography>
                                          <Typography
                                            $type="PMini"
                                            $fontSizeSm="13px"
                                            $fontSizeXs="13px"
                                            $weight="400"
                                            $lineHeight={{ sm: '170%' }}
                                            className="opacity-70 text-nowrap !no-underline font-jamjuree"
                                          >
                                            {elements.description}
                                          </Typography>
                                        </div>

                                        {elements.comingSoon && (
                                          <div>
                                            <Typography
                                              $type="PMini"
                                              $fontSizeSm="10px"
                                              $fontSizeXs="10px"
                                              $weight="600"
                                              className="uppercase bg-[#C4D1CE] rounded-full px-2 py-1 inline"
                                            >
                                              soon
                                            </Typography>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </Link>
                                </NavigationMenuLink>
                                {elements.additionalButton?.href && (
                                  <NavigationMenuLink
                                    className="h-full [&_#menu-title]:hover:underline underline-offset-2"
                                    asChild
                                    aria-disabled={elements.additionalButton.comingSoon}
                                  >
                                    <Link
                                      prefetch={false}
                                      className="flex flex-col justify-between !no-underline"
                                      href={elements.additionalButton?.href}
                                    >
                                      <div className="flex items-center gap-3">
                                        {elements.icon && (
                                          <div className="h-[35px] w-[35px] pointer-events-none">
                                            {typeof elements.additionalButton.icon === 'string' ? (
                                              <Image
                                                width="0"
                                                height="0"
                                                sizes="100vw"
                                                className={'w-full h-full object-contain'}
                                                alt={`${elements.title} image`}
                                                src={elements.additionalButton?.icon}
                                              />
                                            ) : (
                                              elements.additionalButton?.icon
                                            )}
                                          </div>
                                        )}
                                        <div className="flex flex-col gap-2">
                                          <div className="flex flex-col gap-0.5">
                                            <Typography
                                              id="menu-title"
                                              as={'span'}
                                              $type="PSmall"
                                              $fontSizeSm="13px"
                                              $fontSizeXs="13px"
                                              $weight="600"
                                              className={clsx('text-nowrap font-jamjuree')}
                                            >
                                              {elements.additionalButton?.title}
                                            </Typography>
                                            {elements.additionalButton.description && (
                                              <Typography
                                                $type="PMini"
                                                $fontSizeSm="13px"
                                                $fontSizeXs="13px"
                                                $weight="400"
                                                className="opacity-70 text-nowrap !no-underline font-jamjuree"
                                              >
                                                {elements.additionalButton.description}
                                              </Typography>
                                            )}
                                          </div>

                                          {elements.additionalButton.comingSoon && (
                                            <div>
                                              <Typography
                                                $type="PMini"
                                                $fontSizeSm="10px"
                                                $fontSizeXs="10px"
                                                $weight="600"
                                                className="uppercase bg-[#C4D1CE] rounded-full px-2 py-1 inline"
                                              >
                                                soon
                                              </Typography>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </Link>
                                  </NavigationMenuLink>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })}
              </NavigationMenuContent>
            </NavigationMenuItem>
          ) : (
            <NavigationMenuItem
              key={`desktop-menu-${item.id}-${item.title}`}
              className={clsx(style.desktopViewLi, 'relative')}
            >
              <div
                className={clsx(
                  pathname === item.href && 'h-[3px] w-full bg-ebony absolute -bottom-4'
                )}
              />
              <p className={clsx('text-nowrap text-ebony opacity-0 font-semibold')}>{item.title}</p>
              <Link
                prefetch={false}
                className={clsx(
                  pathname !== item.href && 'opacity-80 hover:opacity-100',
                  pathname === item.href && 'selected !font-semibold',
                  'text-nowrap text-ebony absolute left-1/2 transform -translate-x-1/2 hover:!font-semibold z-50 transition'
                )}
                href={item?.href || ''}
              >
                {item.title}
              </Link>
            </NavigationMenuItem>
          );
        })}
        <div className="flex gap-2.5 items-center justify-end flex-1">
          <NavigationMenuItem
            suppressHydrationWarning
            key={`pricing`}
            className={clsx(style.desktopViewLi, 'relative mr-2.5 max-lg:hidden')}
          >
            <div
              className={clsx(
                pathname === PATH_PRICING.ROOT && 'h-[3px] w-full bg-ebony absolute -bottom-4'
              )}
            />
            <p className={clsx('text-nowrap text-ebony opacity-0 font-semibold')}>Pricing</p>
            <Link
              prefetch={false}
              className={clsx(
                pathname !== PATH_PRICING.ROOT && 'opacity-80 hover:opacity-100',
                pathname === PATH_PRICING.ROOT && 'selected !font-semibold',
                'text-nowrap text-ebony absolute left-1/2 transform -translate-x-1/2 hover:!font-semibold z-50 transition'
              )}
              href={PATH_PRICING.ROOT}
            >
              Pricing
            </Link>
          </NavigationMenuItem>
          <NavigationMenuItem suppressHydrationWarning className="max-lg:!hidden">
            <Link href={PRODUCT_DEMO.ROOT} prefetch={false}>
              <button
                className={`green-button hover:bg-[#1B3039] transition ${style.headerDemoButton}`}
              >
                <div className="button-text">
                  <span className="text-white">See in Action</span>
                </div>
              </button>
            </Link>
          </NavigationMenuItem>
          <NavigationMenuItem suppressHydrationWarning>
            <Link href={PATH_DEMO.ROOT} prefetch={false}>
              <button className={`green-button`}>
                <div className="button-text">
                  <span>Request Demo</span>
                </div>
                <div className="gradient"></div>
              </button>
            </Link>
          </NavigationMenuItem>
        </div>
      </NavigationMenuList>
    );
  }
);
